import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Save, BookOpen } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from './LoadingSpinner';

const DictionaryManager = ({ isOpen, onClose, onSelectPhrase }) => {
  const [dictionaries, setDictionaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingPhrases, setEditingPhrases] = useState([]);
  const [newPhrase, setNewPhrase] = useState('');
  const [newDictionaryName, setNewDictionaryName] = useState('');
  const [showNewDictionary, setShowNewDictionary] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchDictionaries();
    }
  }, [isOpen]);

  const fetchDictionaries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dictionaries');
      setDictionaries(response.data);
    } catch (error) {
      console.error('Błąd podczas pobierania słowników:', error);
      toast.error('Nie udało się pobrać słowników');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDictionary = async () => {
    if (!newDictionaryName.trim()) {
      toast.error('Podaj nazwę słownika');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/dictionaries', {
        name: newDictionaryName,
        phrases: []
      });
      setDictionaries([...dictionaries, response.data]);
      setNewDictionaryName('');
      setShowNewDictionary(false);
      toast.success('Słownik został utworzony');
    } catch (error) {
      console.error('Błąd podczas tworzenia słownika:', error);
      toast.error('Nie udało się utworzyć słownika');
    } finally {
      setSaving(false);
    }
  };

  const handleStartEdit = (dictionary) => {
    setEditingId(dictionary.id);
    setEditingName(dictionary.name);
    setEditingPhrases([...dictionary.phrases]);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingPhrases([]);
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim()) {
      toast.error('Podaj nazwę słownika');
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/dictionaries/${editingId}`, {
        name: editingName,
        phrases: editingPhrases
      });
      // Aktualizuj lokalny stan z danymi z serwera
      setDictionaries(dictionaries.map(dict => 
        dict.id === editingId ? response.data : dict
      ));
      handleCancelEdit();
      toast.success('Słownik został zaktualizowany');
      // Odśwież listę słowników aby mieć pewność że dane są aktualne
      await fetchDictionaries();
    } catch (error) {
      console.error('Błąd podczas aktualizacji słownika:', error);
      toast.error('Nie udało się zaktualizować słownika');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDictionary = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten słownik?')) {
      return;
    }

    try {
      await api.delete(`/dictionaries/${id}`);
      setDictionaries(dictionaries.filter(dict => dict.id !== id));
      toast.success('Słownik został usunięty');
    } catch (error) {
      console.error('Błąd podczas usuwania słownika:', error);
      toast.error('Nie udało się usunąć słownika');
    }
  };

  const handleAddPhrase = () => {
    if (!newPhrase.trim()) {
      return;
    }
    setEditingPhrases([...editingPhrases, newPhrase.trim()]);
    setNewPhrase('');
  };

  const handleRemovePhrase = (index) => {
    setEditingPhrases(editingPhrases.filter((_, i) => i !== index));
  };

  const handleSelectPhrase = (phrase) => {
    if (onSelectPhrase) {
      onSelectPhrase(phrase);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <BookOpen className="h-6 w-6 text-blue-600" />
              <h3 className="text-xl font-bold text-gray-900">Moje słowniki</h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="space-y-4">
              {/* Nowy słownik */}
              {showNewDictionary && (
                <div className="border-2 border-dashed border-blue-300 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center space-x-2 mb-3">
                    <input
                      type="text"
                      value={newDictionaryName}
                      onChange={(e) => setNewDictionaryName(e.target.value)}
                      placeholder="Nazwa słownika..."
                      className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyPress={(e) => e.key === 'Enter' && handleCreateDictionary()}
                    />
                    <button
                      onClick={handleCreateDictionary}
                      disabled={saving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {saving ? <LoadingSpinner size="sm" /> : 'Utwórz'}
                    </button>
                    <button
                      onClick={() => {
                        setShowNewDictionary(false);
                        setNewDictionaryName('');
                      }}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Anuluj
                    </button>
                  </div>
                </div>
              )}

              {/* Lista słowników */}
              {dictionaries.length === 0 && !showNewDictionary ? (
                <div className="text-center py-12">
                  <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">Nie masz jeszcze żadnych słowników</p>
                  <button
                    onClick={() => setShowNewDictionary(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    <Plus className="h-4 w-4 inline mr-2" />
                    Utwórz pierwszy słownik
                  </button>
                </div>
              ) : (
                dictionaries.map((dictionary) => (
                  <div key={dictionary.id} className="border border-gray-200 rounded-lg p-4">
                    {editingId === dictionary.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                        />
                        <div className="space-y-2">
                          {editingPhrases.map((phrase, index) => (
                            <div key={index} className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={phrase}
                                onChange={(e) => {
                                  const updated = [...editingPhrases];
                                  updated[index] = e.target.value;
                                  setEditingPhrases(updated);
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <button
                                onClick={() => handleRemovePhrase(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={newPhrase}
                              onChange={(e) => setNewPhrase(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleAddPhrase()}
                              placeholder="Dodaj nową frazę..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={handleAddPhrase}
                              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="flex justify-end space-x-2 pt-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            Anuluj
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            disabled={saving}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
                          >
                            {saving ? <LoadingSpinner size="sm" /> : <Save className="h-4 w-4" />}
                            <span>Zapisz</span>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="font-semibold text-gray-900">{dictionary.name}</h4>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleStartEdit(dictionary)}
                              className="text-blue-600 hover:text-blue-800"
                              title="Edytuj słownik"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteDictionary(dictionary.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Usuń słownik"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {!dictionary.phrases || dictionary.phrases.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">Brak fraz</p>
                          ) : (
                            dictionary.phrases.map((phrase, index) => (
                              <div
                                key={index}
                                onClick={() => handleSelectPhrase(phrase)}
                                className="px-3 py-2 bg-gray-50 rounded-lg hover:bg-blue-50 hover:border-blue-200 border border-transparent cursor-pointer transition-colors"
                                title="Kliknij aby wstawić frazę do opisu"
                              >
                                <p className="text-sm text-gray-700">{phrase}</p>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Przycisk dodania nowego słownika */}
              {!showNewDictionary && dictionaries.length > 0 && (
                <button
                  onClick={() => setShowNewDictionary(true)}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="h-5 w-5" />
                  <span>Dodaj nowy słownik</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DictionaryManager;
